import React, { useMemo } from "react"
import StatusPill from "../../../shared/components/StatusPill"
import { ui } from "../../../shared/styles/uiSystem"
import { SectionCard, ClinicalQrToolsCard } from "./ClinicalHistoryAtoms"
import { buttonStyle, primaryButtonStyle } from "./ClinicalHistoryStyles"
import ClinicalPatientHero from "./ClinicalPatientHero"

function SummaryActionCard({
  eyebrow,
  title,
  description,
  meta,
  badgeText,
  badgeTone = "neutral",
  primaryLabel,
  primaryTone = "secondary",
  onPrimary,
  secondaryLabel,
  onSecondary,
}) {
  return (
    <div
      style={{
        ...ui.card,
        borderRadius: 18,
        padding: 14,
        minHeight: 144,
        display: "grid",
        gap: 10,
        alignContent: "space-between",
      }}
    >
      <div style={{ display: "grid", gap: 8 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {eyebrow ? (
            <div
              style={{
                fontSize: 11,
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: "#64748b",
              }}
            >
              {eyebrow}
            </div>
          ) : null}

          {badgeText ? <StatusPill tone={badgeTone}>{badgeText}</StatusPill> : null}
        </div>

        <div
          style={{
            fontSize: 17,
            fontWeight: 900,
            color: "#0f172a",
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </div>

        {description ? (
          <div
            style={{
              fontSize: 13,
              color: "#475569",
              lineHeight: 1.5,
            }}
          >
            {description}
          </div>
        ) : null}

        {meta ? (
          <div
            style={{
              fontSize: 12,
              color: "#64748b",
              fontWeight: 700,
              lineHeight: 1.45,
            }}
          >
            {meta}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {primaryLabel ? (
          <button
            onClick={onPrimary}
            style={primaryTone === "primary" ? primaryButtonStyle : buttonStyle}
          >
            {primaryLabel}
          </button>
        ) : null}

        {secondaryLabel ? (
          <button onClick={onSecondary} style={buttonStyle}>
            {secondaryLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ActivityRow({
  title,
  description,
  timestamp,
  statusLabel,
  statusTone,
  onAction,
}) {
  return (
    <div
      style={{
        ...ui.card,
        borderRadius: 14,
        padding: 12,
        display: "grid",
        gridTemplateColumns: "minmax(0,1fr) auto",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0, display: "grid", gap: 4 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 800,
            color: "#0f172a",
            lineHeight: 1.35,
          }}
        >
          {title}
        </div>

        {description ? (
          <div
            style={{
              fontSize: 12.5,
              color: "#475569",
              lineHeight: 1.45,
            }}
          >
            {description}
          </div>
        ) : null}

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {timestamp ? (
            <div
              style={{
                fontSize: 12,
                color: "#64748b",
                fontWeight: 700,
              }}
            >
              {timestamp}
            </div>
          ) : null}

          {statusLabel ? (
            <StatusPill tone={statusTone || "neutral"}>{statusLabel}</StatusPill>
          ) : null}
        </div>
      </div>

      {onAction ? (
        <button onClick={onAction} style={buttonStyle}>
          Abrir
        </button>
      ) : null}
    </div>
  )
}

function EmptyState({ text, minHeight = 100 }) {
  return (
    <div
      style={{
        ...ui.emptyState,
        minHeight,
        background: "#ffffff",
      }}
    >
      {text}
    </div>
  )
}

function normalizeStatusKey(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_")
}

function isTruePendingHistoryItem(item) {
  const status = normalizeStatusKey(item?.statusLabel)

  return (
    status === "ABIERTO" ||
    status === "DRAFT" ||
    status === "BORRADOR" ||
    status === "EN_SEGUIMIENTO" ||
    status === "SEGUIMIENTO" ||
    status === "FOLLOW_UP"
  )
}

export default function ClinicalHistoryPatientSummaryView({
  patientDisplayName,
  hub,
  age,
  form,
  isFormDirty,
  isReadOnly,
  readOnlyReason,
  saving,
  dismissedQrReviewId,

  onBackToLauncher,
  onOpenPatientsProfile,
  onOpenOdontogram,
  onOpenBaseEditor,
  onOpenSpecialtyCases,

  qrLoading,
  qrGenerating,
  qrInfo,
  qrImageUrl,
  qrImageLoading,
  onGenerateQrLink,
  onCopyQrLink,
  onOpenQrLink,
  onDownloadQr,
  onPrintQrPoster,
  onOpenUpgrade,

  hubFormHistoryItems,
  hubTimelineItems,

  onHubHistoryAction,
  onHubHistorySecondaryAction,
}) {
  const qrReviewId = hub?.qrIntake?.latestInstanceId || ""
  const qrReviewStatus = String(
    hub?.qrIntake?.latestStatus || hub?.qrIntake?.status || ""
  ).toUpperCase()

  const hasPendingQrReview = Boolean(
    qrReviewId &&
      qrReviewId !== dismissedQrReviewId &&
      !hub?.qrIntake?.isReviewed &&
      !hub?.qrIntake?.reviewedAt &&
      !hub?.qrIntake?.latestReviewedAt &&
      qrReviewStatus !== "REVIEWED" &&
      qrReviewStatus !== "RESOLVED"
  )

  const pendingActions = useMemo(() => {
    const items = []

    if (hasPendingQrReview) {
      items.push({
        id: "qr-pending",
        eyebrow: "Pre-registro",
        title: "Revisar captura QR del paciente",
        description:
          "Hay una captura pendiente de validación antes de incorporarla al expediente clínico.",
        meta: hub?.qrIntake?.latestSubmittedAt
          ? `Recibida: ${new Date(hub.qrIntake.latestSubmittedAt).toLocaleString("es-MX")}`
          : "Lista para revisión.",
        badgeText: "Por revisar",
        badgeTone: "warning",
        primaryLabel: "Revisar captura",
        primaryTone: "primary",
        onPrimary: () => onHubHistoryAction("OPEN_QR_REVIEW"),
      })
    }

    if (!hub?.summary?.baseHistoryExists) {
      items.push({
        id: "base-history-missing",
        eyebrow: "Historia base",
        title: "Completar historia base general",
        description:
          "Todavía no existe una historia clínica base consolidada para este paciente.",
        meta: "Conviene capturarla antes de seguir profundizando en especialidades.",
        badgeText: "Sin iniciar",
        badgeTone: "info",
        primaryLabel: "Abrir historia base",
        primaryTone: "primary",
        onPrimary: onOpenBaseEditor,
      })
    } else if (isFormDirty) {
      items.push({
        id: "base-history-dirty",
        eyebrow: "Historia base",
        title: "Hay cambios pendientes por consolidar",
        description:
          "La historia base tiene cambios en memoria que todavía no forman parte del registro definitivo.",
        meta: "Conviene revisar y guardar desde el editor base.",
        badgeText: "Cambios",
        badgeTone: "warning",
        primaryLabel: "Abrir historia base",
        primaryTone: "primary",
        onPrimary: onOpenBaseEditor,
      })
    }

    const prioritizedHub = (hubFormHistoryItems || [])
      .filter((item) => isTruePendingHistoryItem(item))
      .slice(0, 2)

    prioritizedHub.forEach((item, index) => {
      items.push({
        id: item.id || `hub-step-${index}`,
        eyebrow: "Continuidad clínica",
        title: item.title || "Acción clínica",
        description: item.summary || item.subtitle || "",
        meta: item.updatedAt || item.sourceLabel || "",
        badgeText: item.statusLabel || "",
        badgeTone: item.statusTone || "neutral",
        primaryLabel: item.primaryLabel || "Abrir",
        primaryTone: "secondary",
        onPrimary: () => onHubHistoryAction(item.action),
        secondaryLabel: item.secondaryLabel || "",
        onSecondary: item.secondaryAction
          ? () => onHubHistorySecondaryAction(item)
          : undefined,
      })
    })

    return items
  }, [
    hasPendingQrReview,
    hub,
    hubFormHistoryItems,
    isFormDirty,
    onHubHistoryAction,
    onHubHistorySecondaryAction,
    onOpenBaseEditor,
  ])

  const recentActivity = useMemo(() => {
    return (hubTimelineItems || []).slice(0, 4)
  }, [hubTimelineItems])

  const clinicalTools = useMemo(() => {
    return [
      {
        id: "tool-base",
        title: "Historia base",
        description: hub?.summary?.baseHistoryExists
          ? "Expediente general del paciente."
          : "Aún no existe historia base consolidada.",
        meta: hub?.summary?.latestBaseHistoryAt
          ? `Última actualización: ${new Date(hub.summary.latestBaseHistoryAt).toLocaleDateString("es-MX")}`
          : "Sin registro reciente.",
        badgeText: "Herramienta",
        badgeTone: "neutral",
        primaryLabel: "Abrir",
        primaryTone: "secondary",
        onPrimary: onOpenBaseEditor,
      },
      {
        id: "tool-odontogram",
        title: "Odontograma",
        description: "Exploración gráfica y continuidad sobre hallazgos por pieza.",
        meta: "Acceso clínico directo al contexto dental del paciente.",
        badgeText: "Visual",
        badgeTone: "neutral",
        primaryLabel: "Abrir",
        primaryTone: "secondary",
        onPrimary: () => onOpenOdontogram(false),
      },
      {
        id: "tool-diagnosis",
        title: "Diagnóstico inicial",
        description:
          "Registra hallazgos iniciales y define la conducta clínica sugerida.",
        meta: "Puente entre historia base, odontograma y tratamiento.",
        badgeText: "Clínico",
        badgeTone: "neutral",
        primaryLabel: "Abrir",
        primaryTone: "secondary",
        onPrimary: () => onHubHistoryAction("OPEN_DIAGNOSIS"),
      },
      {
        id: "tool-endo",
        title: "Endodoncia",
        description: "Consulta y organiza casos endodónticos del paciente.",
        meta: "Continuidad clínica especializada.",
        badgeText: "Casos",
        badgeTone: "neutral",
        primaryLabel: "Ver casos",
        primaryTone: "secondary",
        onPrimary: () => onOpenSpecialtyCases("ENDO"),
      },
      {
        id: "tool-perio",
        title: "Periodoncia",
        description: "Consulta y organiza casos periodontales.",
        meta: "Continuidad clínica especializada.",
        badgeText: "Casos",
        badgeTone: "neutral",
        primaryLabel: "Ver casos",
        primaryTone: "secondary",
        onPrimary: () => onOpenSpecialtyCases("PERIO"),
      },
      {
        id: "tool-surgery",
        title: "Cirugía",
        description: "Accede a los casos quirúrgicos del paciente.",
        meta: "Continuidad clínica especializada.",
        badgeText: "Casos",
        badgeTone: "neutral",
        primaryLabel: "Ver casos",
        primaryTone: "secondary",
        onPrimary: () => onOpenSpecialtyCases("SURGERY"),
      },
      {
        id: "tool-ortho",
        title: "Ortodoncia",
        description: "Consulta casos ortodónticos y seguimiento.",
        meta: "Continuidad clínica especializada.",
        badgeText: "Casos",
        badgeTone: "neutral",
        primaryLabel: "Ver casos",
        primaryTone: "secondary",
        onPrimary: () => onOpenSpecialtyCases("ORTHO"),
      },
      {
        id: "tool-pediatric",
        title: "Odontopediatría",
        description: "Consulta casos pediátricos y seguimiento.",
        meta: "Continuidad clínica especializada.",
        badgeText: "Casos",
        badgeTone: "neutral",
        primaryLabel: "Ver casos",
        primaryTone: "secondary",
        onPrimary: () => onOpenSpecialtyCases("PEDIATRIC"),
      },
    ]
  }, [hub, onHubHistoryAction, onOpenBaseEditor, onOpenOdontogram, onOpenSpecialtyCases])

  return (
    <div
      style={{
        display: "grid",
        gap: 18,
        alignContent: "start",
      }}
    >
      <ClinicalPatientHero
        patientDisplayName={patientDisplayName}
        hub={hub}
        age={age}
        form={form}
        isFormDirty={isFormDirty}
        isReadOnly={isReadOnly}
        readOnlyReason={readOnlyReason}
        saving={saving}
        onBackToLauncher={onBackToLauncher}
        onOpenPatientsProfile={onOpenPatientsProfile}
        onOpenBaseEditor={onOpenBaseEditor}
        onOpenOdontogram={onOpenOdontogram}
      />

      {pendingActions.length ? (
        <SectionCard
          title="Pendientes clínicos"
          subtitle="Solo acciones que siguen abiertas o requieren atención"
          badgeText={`${pendingActions.length} ${pendingActions.length === 1 ? "pendiente" : "pendientes"}`}
          badgeTone="warning"
          defaultOpen={true}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 12,
            }}
          >
            {pendingActions.map((item) => (
              <SummaryActionCard
                key={item.id}
                eyebrow={item.eyebrow}
                title={item.title}
                description={item.description}
                meta={item.meta}
                badgeText={item.badgeText}
                badgeTone={item.badgeTone}
                primaryLabel={item.primaryLabel}
                primaryTone={item.primaryTone}
                onPrimary={item.onPrimary}
                secondaryLabel={item.secondaryLabel}
                onSecondary={item.onSecondary}
              />
            ))}
          </div>
        </SectionCard>
      ) : null}

      <SectionCard
        title="Herramientas clínicas"
        subtitle="Accesos directos a los módulos clínicos del paciente"
        badgeText={`${clinicalTools.length} accesos`}
        badgeTone="success"
        defaultOpen={true}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 12,
          }}
        >
          {clinicalTools.map((item) => (
            <SummaryActionCard
              key={item.id}
              eyebrow="Herramienta"
              title={item.title}
              description={item.description}
              meta={item.meta}
              badgeText={item.badgeText}
              badgeTone={item.badgeTone}
              primaryLabel={item.primaryLabel}
              primaryTone={item.primaryTone}
              onPrimary={item.onPrimary}
            />
          ))}
        </div>
      </SectionCard>

      <SectionCard
        title="Actividad reciente"
        subtitle="Últimos movimientos clínicos del paciente"
        badgeText={`${recentActivity.length} ${recentActivity.length === 1 ? "evento" : "eventos"}`}
        badgeTone="neutral"
        defaultOpen={true}
      >
        {recentActivity.length ? (
          <div style={{ display: "grid", gap: 8 }}>
            {recentActivity.map((item, index) => (
              <ActivityRow
                key={item.id || `activity-${index}`}
                title={item.title || "Evento clínico"}
                description={item.description || ""}
                timestamp={item.timestamp || ""}
                statusLabel={item.statusLabel || ""}
                statusTone={item.statusTone || "neutral"}
                onAction={item.action ? () => onHubHistoryAction(item.action) : undefined}
              />
            ))}
          </div>
        ) : (
          <EmptyState text="Aún no hay actividad reciente suficiente para mostrar continuidad clínica en este paciente." />
        )}
      </SectionCard>

      <SectionCard
        title="Herramientas de ingreso del paciente"
        subtitle="Acciones administrativas relacionadas al pre-registro"
        defaultOpen={false}
      >
        <ClinicalQrToolsCard
          qrLoading={qrLoading}
          qrGenerating={qrGenerating}
          qrInfo={qrInfo}
          qrImageUrl={qrImageUrl}
          qrImageLoading={qrImageLoading}
          isReadOnly={isReadOnly}
          readOnlyReason={readOnlyReason}
          onGenerate={onGenerateQrLink}
          onCopy={onCopyQrLink}
          onOpen={onOpenQrLink}
          onDownload={onDownloadQr}
          onPrint={onPrintQrPoster}
          onUpgrade={onOpenUpgrade}
        />
      </SectionCard>
    </div>
  )
}